import { partials } from "@shopify/partial-rendering";

/**
 * <s-recently-viewed> — remembers products a visitor opens and shows the last few on product pages.
 *
 * Required markup:
 *   data-handle / data-url      — current product (recorded, and excluded from the list).
 *   data-limit                  — max cards to show.
 *   [data-rv-list]              — <ul> the cards are appended to.
 *
 * Each card is the server-rendered {% partial 'recently-viewed-card' %} of that product's page,
 * so images and money-formatted prices come from Liquid. Storage is the visitor's own browser.
 */
const STORAGE_KEY = "zalooq:recently-viewed";
const MAX_STORED = 12;
const PARTIAL = "recently-viewed-card";

class SRecentlyViewed extends HTMLElement {
  #controller = null;

  connectedCallback() {
    const handle = this.dataset.handle;
    const url = this.dataset.url;
    const history = this.#read().filter((item) => item && item.handle && item.url);

    // Show what was viewed before this page, then record this page for next time.
    const limit = Number(this.dataset.limit) || 4;
    const previous = history.filter((item) => item.handle !== handle).slice(0, limit);
    if (handle && url) {
      this.#write([{ handle, url }, ...history.filter((item) => item.handle !== handle)].slice(0, MAX_STORED));
    }

    if (previous.length > 0) this.#render(previous);
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #read() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  #write(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Storage unavailable (private mode / full): the section simply stays hidden.
    }
  }

  async #render(items) {
    const list = this.querySelector("[data-rv-list]");
    if (!list) return;

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const results = await Promise.all(items.map((item) => this.#fetchCard(item.url, signal)));
    if (signal.aborted) return;

    const cards = results.filter(Boolean);
    // Forget products that no longer resolve (deleted or unpublished).
    const missing = new Set(items.filter((_, i) => !results[i]).map((item) => item.handle));
    if (missing.size > 0) this.#write(this.#read().filter((item) => !missing.has(item.handle)));

    if (cards.length === 0) return;
    for (const card of cards) {
      const li = document.createElement("li");
      li.append(card);
      list.append(li);
    }
    this.hidden = false;
  }

  async #fetchCard(url, signal) {
    try {
      const html = await partials.fetch(PARTIAL, { url, signal });
      const card = this.#extract(typeof html === "string" ? html : "");
      if (card) return card;
    } catch (error) {
      if (signal.aborted) return null;
    }

    // Fallback: read the card from the full product page.
    try {
      const response = await fetch(url, { signal });
      if (!response.ok) return null;
      return this.#extract(await response.text());
    } catch {
      return null;
    }
  }

  #extract(html) {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const direct = doc.querySelector("[data-rv-card]");
    if (direct) return document.importNode(direct, true);
    // The card lives inside a <template> on full pages.
    for (const template of doc.querySelectorAll("template")) {
      const card = template.content.querySelector("[data-rv-card]");
      if (card) return document.importNode(card, true);
    }
    return null;
  }
}

if (!customElements.get("s-recently-viewed")) {
  customElements.define("s-recently-viewed", SRecentlyViewed);
}
